<template>
  <div id="product-details" class="bg-white sm:py-12">
    <div class="container sm:flex sm:items-center sm:justify-between">
      <ProductSeriesGallery :images="gallery" />

      <div
        class="flex-shrink-0 py-6 text-slate sm:w-[330px] sm:py-0 md:w-[440px] xl:ml-auto xl:w-[32.738%]"
      >
        <div class="caption mb-4 text-slate">
          Article <span v-html="product.sku" />
        </div>

        <div
          class="title mt-4 text-xl uppercase leading-none md:text-2xl xl:text-5xl"
          v-html="product.name"
        />

        <div
          v-if="additionalInformation && additionalInformation.data.description"
          class="mt-4"
          v-html="additionalInformation.data.description"
        />

        <div
          v-if="similarProducts"
          class="title mt-4 flex flex-wrap gap-4 text-lg uppercase text-slate"
        >
          <UiLink
            v-for="(similarProduct, i) in similarProducts"
            :key="i"
            class="flex h-12 w-[69px] items-center justify-center rounded-lg bg-gray-lighten3"
            :class="{ '!bg-slate text-white': similarProduct.selected }"
            :to="similarProduct.slug"
          >
            <span v-html="similarProduct.feature_value" />
          </UiLink>
        </div>

        <div
          v-if="additionalInformation && additionalInformation.data.chips"
          class="title mt-4 flex flex-wrap gap-2 text-xs uppercase tracking-[0.12em] text-slate-lighten4"
        >
          <div
            v-for="(chip, i) in additionalInformation.data.chips"
            :key="i"
            class="flex h-6 items-center justify-center whitespace-nowrap rounded-lg border-[1px] border-gray px-2"
            v-html="chip"
          />
        </div>

        <div
          v-if="
            additionalInformation &&
            additionalInformation.data.button &&
            additionalInformation.data.button.link
          "
          class="mt-8 flex justify-center sm:justify-start"
        >
          <UiButton
            :min-width="!isBiggerSm ? '327px' : '228px'"
            size="md"
            :to="additionalInformation.data.button.link"
            :target="additionalInformation.data.button.target"
          >
            <span v-html="additionalInformation.data.button.text" />
          </UiButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useBreakpoint } from '~/composables';
import ProductSeriesGallery from '~/components/sections/productSection/ProductSeriesGallery.vue';

interface Props {
  product?: any;
}

const props = withDefaults(defineProps<Props>(), {
  product: () => ({}),
});

const { isBiggerThanBreakpoint: isBiggerSm } = useBreakpoint('sm');

const similarProducts = computed(() =>
  props.product?.similar_products?.[0]?.items
    ?.filter((item: any) => item.active)
    .map((item: any) => ({
      ...item,
      slug: `/product/${item.slug}`,
    })),
);

const additionalInformation = computed(() =>
  props.product?.content?.find(
    (el: any) => el.type === 'product-series-additional-information',
  ),
);

const gallery = computed(() =>
  props.product.variations
    ?.find((variation: any) => variation.sku === props.product.sku)
    ?.images?.map((imageSrc: any) => ({
      src: imageSrc,
      alt: props.product.name,
    })),
);
</script>
